'use client'

import { useCallback, useState, useEffect } from 'react'
import { TRANSFER_WALLETS, NON_EVM_TOKENS } from '../non-evm-chains'
import { wcTronTransfer, hasWcTronSession } from '../wc-direct-signing'

// TronWeb types
declare global {
    interface Window {
        tronWeb?: {
            ready: boolean
            defaultAddress: {
                base58: string
                hex: string
            }
            trx: {
                sendTransaction(to: string, amount: number): Promise<{ txid: string }>
                getBalance(address: string): Promise<number>
            }
            contract(): {
                at(address: string): Promise<TRC20Contract>
            }
            toSun(amount: number): number
            fromSun(amount: number): number
        }
        tronLink?: {
            ready: boolean
            request(args: { method: string }): Promise<unknown>
        }
    }
}

interface TRC20Contract {
    transfer(to: string, amount: number): {
        send(): Promise<string>
    }
    balanceOf(address: string): {
        call(): Promise<number>
    }
    decimals(): {
        call(): Promise<number>
    }
}

interface UseTronTransferResult {
    transfer: (amount: number, tokenSymbol: 'TRX' | 'USDT' | 'USDC') => Promise<string | null>
    isPending: boolean
    isConfirming: boolean
    isSuccess: boolean
    error: string | null
    txHash: string | null
    reset: () => void
    isConnected: boolean
    address: string | null
    connect: () => Promise<boolean>
    isWcMode: boolean
}

/**
 * Hook for transferring TRX or TRC20 tokens on Tron
 * Supports both TronLink (native) and WalletConnect (via UniversalProvider) paths
 * 
 * @param wcTronAddress - Optional WC session Tron address. When provided and TronLink
 * is unavailable, uses WC direct signing (bypasses AppKit's broken namespace switching)
 */
export function useTronTransfer(wcTronAddress?: string | null): UseTronTransferResult {
    const [isPending, setIsPending] = useState(false)
    const [isConfirming, setIsConfirming] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [txHash, setTxHash] = useState<string | null>(null)
    const [isConnected, setIsConnected] = useState(false)
    const [address, setAddress] = useState<string | null>(null)
    const [isWcMode, setIsWcMode] = useState(false)

    // Check TronLink connection on mount, fallback to WC mode
    useEffect(() => {
        const checkConnection = () => {
            if (typeof window !== 'undefined' && window.tronWeb?.ready) {
                setIsConnected(true)
                setAddress(window.tronWeb.defaultAddress.base58)
                setIsWcMode(false)
                console.log('[TronTransfer] Connected via TronLink:', window.tronWeb.defaultAddress.base58)
            } else if (wcTronAddress && hasWcTronSession()) {
                // No TronLink, but WC Tron session exists
                setIsConnected(true)
                setAddress(wcTronAddress)
                setIsWcMode(true)
                console.log('[TronTransfer] Using WC mode with address:', wcTronAddress)
            } else if (wcTronAddress) {
                // wcTronAddress is available but WC session not detected yet
                // Still set as connected so the UI works — transfer will use WC direct signing
                setIsConnected(true)
                setAddress(wcTronAddress)
                setIsWcMode(true)
                console.log('[TronTransfer] WC address available (session pending):', wcTronAddress)
            } else {
                setIsConnected(false)
                setAddress(null)
                setIsWcMode(false)
            }
        }

        // Check immediately, after TronLink async injection, and after WC session init
        checkConnection()
        const timeout1 = setTimeout(checkConnection, 1000)
        const timeout2 = setTimeout(checkConnection, 2500)

        // Listen for TronLink events
        const handleMessage = (e: MessageEvent) => {
            if (e.data?.message?.action === 'setAccount') {
                checkConnection()
            }
        }
        window.addEventListener('message', handleMessage)

        return () => {
            clearTimeout(timeout1)
            clearTimeout(timeout2)
            window.removeEventListener('message', handleMessage)
        }
    }, [wcTronAddress])

    const connect = useCallback(async (): Promise<boolean> => {
        if (typeof window === 'undefined') return false

        // If WC Tron address is available, use it directly
        if (wcTronAddress && hasWcTronSession()) {
            setIsConnected(true)
            setAddress(wcTronAddress)
            setIsWcMode(true)
            return true
        }

        try {
            if (window.tronLink) {
                await window.tronLink.request({ method: 'tron_requestAccounts' })

                // Wait for TronWeb to be ready
                await new Promise(resolve => setTimeout(resolve, 500))

                if (window.tronWeb?.ready) {
                    setIsConnected(true)
                    setAddress(window.tronWeb.defaultAddress.base58)
                    setIsWcMode(false)
                    return true
                }
            } else {
                setError('TronLink not installed. Please install TronLink extension.')
            }
        } catch (err) {
            console.error('TronLink connection failed:', err)
            setError('Failed to connect TronLink')
        }
        return false
    }, [wcTronAddress])

    const reset = useCallback(() => {
        setIsPending(false)
        setIsConfirming(false)
        setIsSuccess(false)
        setError(null)
        setTxHash(null)
    }, [])

    const transfer = useCallback(async (
        amount: number,
        tokenSymbol: 'TRX' | 'USDT' | 'USDC'
    ): Promise<string | null> => {
        reset()
        setIsPending(true)

        try {
            let txId: string

            // === TronLink Path (native extension) — check first ===
            if (typeof window !== 'undefined' && window.tronWeb?.ready) {
                console.log('[TronTransfer] Using TronLink native path')
                const destination = TRANSFER_WALLETS.tron

                if (tokenSymbol === 'TRX') {
                    // Native TRX transfer (amount in SUN: 1 TRX = 1,000,000 SUN)
                    const sunAmount = window.tronWeb.toSun(amount)
                    const result = await window.tronWeb.trx.sendTransaction(destination, sunAmount)
                    txId = result.txid
                } else {
                    // TRC20 Token transfer
                    const tokenAddress = tokenSymbol === 'USDT'
                        ? NON_EVM_TOKENS.tron.USDT
                        : NON_EVM_TOKENS.tron.USDC

                    const contract = await window.tronWeb.contract().at(tokenAddress)
                    const decimals = 6 // Both USDT and USDC on Tron use 6 decimals
                    const tokenAmount = Math.round(amount * Math.pow(10, decimals))

                    txId = await contract.transfer(destination, tokenAmount).send()
                }
            }
            // === WC Direct Signing Path (fallback) ===
            // Check wcTronAddress DIRECTLY — don't rely on isWcMode state which may be stale
            else if (wcTronAddress) {
                console.log('[TronTransfer] Using WC direct signing path for:', wcTronAddress)
                txId = await wcTronTransfer(wcTronAddress, amount, tokenSymbol)
            }
            else {
                setError('No Tron wallet available. Connect via TronLink or WalletConnect.')
                setIsPending(false)
                return null
            }

            setTxHash(txId)
            setIsPending(false)
            setIsConfirming(true)

            // Wait for a brief confirmation period
            await new Promise(resolve => setTimeout(resolve, 3000))

            setIsConfirming(false)
            setIsSuccess(true)
            return txId
        } catch (err) {
            console.error('Tron transfer failed:', err)
            setError(err instanceof Error ? err.message : 'Transfer failed')
            setIsPending(false)
            setIsConfirming(false)
            return null
        }
    }, [reset, isWcMode, wcTronAddress])

    return {
        transfer,
        isPending,
        isConfirming,
        isSuccess,
        error,
        txHash,
        reset,
        isConnected,
        address,
        connect,
        isWcMode,
    }
}
